import { useApplicationConfigStore } from '~/store/application-config'
import { storeToRefs } from 'pinia'

export function useNavItem() {
  const applicationConfigStore = useApplicationConfigStore()
  const { navItems, completedModuleEventPaths } = storeToRefs(applicationConfigStore)
  const currentRoute = useRoute()

  const activeMatch = computed(() => findActiveMatch(navItems?.value, currentRoute.path))

  const activeRoute = computed(() => {
    const m = activeMatch.value
    if (!m) return ''
    if (m.kind === 'root' || m.kind === 'rootHidden') return m.root.url
    return m.visibleChild.url
  })

  const activeParent = computed(() => {
    const m = activeMatch.value
    return m?.kind === 'visibleChild' ? m.root.url : ''
  })

  const activeSectionLabel = computed(() => {
    const m = activeMatch.value
    if (m?.kind !== 'sectionHidden') return ''
    return m.hiddenChild?.navSectionLabel || ''
  })

  const currentActiveTopLevelLabel = computed(() => {
    const m = activeMatch.value
    return m ? m.root.label : ''
  })

  const currentActiveVisibleChildLabel = computed(() => {
    const m = activeMatch.value
    if (!m) return ''
    if (m.kind === 'visibleChild' || m.kind === 'sectionHidden') return m.visibleChild.label
    return ''
  })

  function isNavItemComplete(item) {
    return _isComplete(item, c => c.completed === true)
  }

  function isNavItemCompleteOptimistic(item) {
    if (!item?.url) return false
    return _isComplete(item, c => completedModuleEventPaths.value.includes(c?.url))
  }

  function _isComplete(item, isDone) {
    if (!item?.visibleChildren?.length && !item?.hiddenChildren?.length) {
      return isDone(item)
    }
    return (isDone(item) || item?.type === 'section')
      && _getAllChildrenForNavItem(item).every(child =>
        // adopted hiddenChildren don't have a completed property
        child?.completed === undefined ? true : isDone(child)
      )
  }

  function _getAllChildrenForNavItem(navItem) {
    const allChildren = []

    if (navItem?.visibleChildren?.length) {
      allChildren.push(...navItem.visibleChildren)
      navItem.visibleChildren.forEach(child => {
        allChildren.push(..._getAllChildrenForNavItem(child))
      })
    }

    if (navItem?.hiddenChildren?.length) {
      allChildren.push(...navItem.hiddenChildren)
      navItem.hiddenChildren.forEach(child => {
        allChildren.push(..._getAllChildrenForNavItem(child))
      })
    }

    return allChildren
  }

  return {
    activeRoute,
    activeParent,
    activeSectionLabel,
    currentActiveTopLevelLabel,
    currentActiveVisibleChildLabel,
    isNavItemComplete,
    isNavItemCompleteOptimistic,
  }
}

// Walks navItems in precedence order (root → visibleChild → navSectionLabel
// hiddenChild → rootHidden) and returns the first match for `path`, or null.
function findActiveMatch(navItems, path) {
  if (!navItems) return null

  for (const root of navItems) {
    if (root.url === path) {
      return { kind: 'root', root }
    }

    const visibleChildren = root.visibleChildren || []
    for (const visibleChild of visibleChildren) {
      if (visibleChild.url === path) {
        return { kind: 'visibleChild', root, visibleChild }
      }
      if (visibleChild?.navSectionLabel) {
        const hiddenChildren = visibleChild.hiddenChildren || []
        for (const hiddenChild of hiddenChildren) {
          if (hiddenChild?.url === path) {
            return { kind: 'sectionHidden', root, visibleChild, hiddenChild }
          }
        }
      }
    }

    const rootHiddenChildren = root.hiddenChildren || []
    for (const rootHiddenChild of rootHiddenChildren) {
      if (rootHiddenChild?.url === path) {
        return { kind: 'rootHidden', root, rootHiddenChild }
      }
    }
  }

  return null
}
